import React, { useState, useMemo } from 'react';
import { useAppSelector } from '@src/core/hooks';
import { downloadBlob } from '@src/common';
import { getOrderVouchers, getSaleVouchers } from '@src/common/vouchers';
import { Order, Sale } from '@src/core/buyerApiClient';
import { orderHasVouchersSelector } from '@src/features/order';
import { currentSaleHasVouchersSelector } from '@src/features/sale';
import Confetti from '@src/assets/images/Confetti.svg';
import { Icon, Message, PaymentDone } from './styles';
import { MdCheck, MdOutlineCloudDownload } from 'react-icons/md';
import Button from '@src/components/ui2/Button';
import useTranslate from '@src/i18n/useTranslate';

type PaymentSuccessProps = {
  order?: Order;
  sale?: Sale;
};

const PaymentSuccess = (props: PaymentSuccessProps) => {
  const { sale, order } = props;

  const { i18n } = useTranslate();
  const [loadingVouchersPdf, setLoadingVouchersPdf] = useState(false);
  const saleHasVouchers = useAppSelector(currentSaleHasVouchersSelector);
  const orderHasVouchers = useAppSelector(orderHasVouchersSelector(order?.id ?? ''));
  const hasVouchers = useMemo(() => {
    if (sale) {
      return saleHasVouchers;
    }

    if (order) {
      return orderHasVouchers;
    }

    return false;
  }, [orderHasVouchers, saleHasVouchers]);

  const downloadVouchers = async () => {
    setLoadingVouchersPdf(true);

    if (sale) {
      const pdfBlob = await getSaleVouchers(sale!.id);
      if (pdfBlob) {
        downloadBlob(pdfBlob, `vouchers-${sale!.id}.pdf`);
      }
    } else if (order) {
      const pdfBlob = await getOrderVouchers(order!.id);
      if (pdfBlob) {
        downloadBlob(pdfBlob, `vouchers-${order!.id}.pdf`);
      }
    }

    setLoadingVouchersPdf(false);
  };

  return (
    <>
      <PaymentDone>
        {i18n('direct_link_payment_success_alert')} <MdCheck />
      </PaymentDone>

      <Icon>
        <Confetti />
      </Icon>

      <Message>{i18n('direct_link_payment_success_message')}</Message>

      {hasVouchers ? (
        <>
          <Message>{i18n('direct_link_payment_success_voucher_message')}</Message>

          <Button
            onClick={() => downloadVouchers()}
            fullWidth
            disabled={loadingVouchersPdf}
            loading={loadingVouchersPdf}
          >
            <MdOutlineCloudDownload />
            {i18n('direct_link_payment_success_download_vouchers')}
          </Button>
        </>
      ) : null}
    </>
  );
};

export default PaymentSuccess;
